import { graphql } from '@/__generated__';
import {
  RankFrequency,
  TestLevelsWithFilterQuery,
  TypeExamTestLevel,
} from '@/__generated__/graphql';
import { TestLevelsWithFilter } from '@/api/common';
import { useQuery } from '@apollo/client';
import { head } from 'lodash-es';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Level {
  id: string;
  name: string;
}

interface Tab {
  id: RankFrequency;
  name: string;
}

export const GetTestLevelRankingDetail = graphql(`
  query GetTestLevelRankingDetail(
    $frequency: RankFrequency!
    $testLevelId: ID!
    $classCode: String
    $first: Int
    $last: Int
    $before: Cursor
    $after: Cursor
    $filterBy: LearnerTestLevelRankFilter
  ) {
    learnerTestLevelRanksDetail: learnerTestLevelRanks(
      frequency: $frequency
      testLevelId: $testLevelId
      classCode: $classCode
      filterBy: $filterBy
      first: $first
      last: $last
      before: $before
      after: $after
    ) {
      nodes {
        rank
        score
        learnerProfile {
          ...learnerProfileRanking
        }
        grade
      }
      totalCount
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`);

function ProfileRanking({ profileId }: { profileId: string }) {
  const [activeTab, setActiveTab] = useState<RankFrequency>(RankFrequency.Daily);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const { t } = useTranslation('common');

  const [selectedLevel, setSelectedLevel] = useState<Level | undefined>(undefined);

  const { data: dataTestLevel } = useQuery<TestLevelsWithFilterQuery>(TestLevelsWithFilter, {
    variables: {
      filterBy: {
        typeTestLevel: [TypeExamTestLevel.Classic],
      },
    },
    onCompleted: (res) => {
      const levelInit = res?.testLevels?.nodes?.find((lv) => lv.slug === 'starters');
      const dataInit = head(res?.testLevels?.nodes);
      if (levelInit?.id) {
        setSelectedLevel({
          id: levelInit?.id,
          name: levelInit.name,
        });
      } else if (dataInit) {
        setSelectedLevel({
          id: dataInit?.id,
          name: dataInit?.name,
        });
      }
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: dataUserRanking } = useQuery(GetTestLevelRankingDetail, {
    variables: {
      first: 1,
      testLevelId: selectedLevel?.id as string,
      frequency: activeTab,
      filterBy: {
        learnerProfileId: [profileId],
      },
    },
    skip: !selectedLevel?.id,
    fetchPolicy: 'cache-and-network',
  });

  const dropdownRef = useRef<HTMLDivElement>(null);

  const tabs: Tab[] = [
    { id: RankFrequency.Daily, name: t('ranking.daily') },
    { id: RankFrequency.Weekly, name: t('ranking.weekly') },
    { id: RankFrequency.Monthly, name: t('ranking.monthly') },
    { id: RankFrequency.Yearly, name: t('ranking.yearly') },
  ];

  const levels = useMemo(() => {
    return (
      dataTestLevel?.testLevels?.nodes?.map((lv) => {
        return {
          id: lv.id,
          name: lv.name,
        };
      }) || []
    );
  }, [dataTestLevel?.testLevels?.nodes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLevelSelect = (level: Level) => {
    setSelectedLevel(level);
    setIsDropdownOpen(false);
  };

  const dataProfile = useMemo(() => {
    return dataUserRanking?.learnerTestLevelRanksDetail?.nodes?.find(
      (el) => el.learnerProfile.learnerProfileId?.toString() === profileId?.toString(),
    );
  }, [dataUserRanking?.learnerTestLevelRanksDetail?.nodes, profileId]);

  return (
    <div className="w-full md:w-[50%] bg-[#F9F8FF] rounded-[8px]">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-[17px] font-normal text-[#5E5E5E]">{t('ranking.rank')}</h2>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center flex-col text-sm text-blue-600 hover:text-blue-700 focus:outline-none"
            >
              <p className="text-[#5E5E5E] text-[14px]">{t('ranking.current_level')}</p>
              <p className="h-[20px] flex justify-end w-full flex-row items-center text-[#321F87] text-[14px] font-bold gap-2">
                <p>{selectedLevel?.name} </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="7"
                  viewBox="0 0 8 7"
                  fill="none"
                >
                  <path
                    d="M4.86603 6.5C4.48113 7.16667 3.51887 7.16667 3.13397 6.5L0.535899 2C0.150999 1.33333 0.632124 0.499999 1.40192 0.499999L6.59808 0.5C7.36788 0.5 7.849 1.33333 7.4641 2L4.86603 6.5Z"
                    fill="#321F87"
                  />
                </svg>
              </p>
            </button>
            {isDropdownOpen && (
              <div className="max-h-[150px] overflow-y-auto absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                {levels.map((level) => (
                  <button
                    type="button"
                    key={level.id}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleLevelSelect(level)}
                  >
                    {level.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex border-b border-gray-200 bg-[#F0EDFF] -mx-4">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              className={`flex-1 py-2 px-4 text-center font-bold ${
                activeTab === tab.id
                  ? 'text-[#6C53DC] border-b-2 border-[#6C53DC] font-[15px]'
                  : 'text-[#C6BFE5]'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.name}
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-center gap-40 items-center">
          <div>
            <p className="text-[14px] text-[#322087]">{t('ranking.scores')}</p>
            <p className="text-[21px] font-bold text-[#5E5E5E]">{dataProfile?.grade || 0}</p>
          </div>
          <div>
            <p className="text-sm text-[#322087]">{t('ranking.rank')}</p>
            <p className="text-[21px] font-bold text-[#5E5E5E]">{dataProfile?.rank || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileRanking;
